<template>
  <div class="pie-chart-container" ref="chartContainer"></div>
</template>

<script setup>
import { ref, onMounted, onUnmounted, watch } from 'vue'
import Plotly from 'plotly.js-dist-min'

const props = defineProps({
  assets: {
    type: Object,
    required: true
  }
})

const chartContainer = ref(null)

function calculatePieData() {
  const sortedAssets = Object.entries(props.assets)
    .sort((a, b) => b[1].value - a[1].value)

  const values = []
  const labels = []
  const colors = []

  sortedAssets.forEach(([key, asset]) => {
    values.push(asset.value / 1000) // Convert to thousands
    labels.push(formatLabel(key))
    colors.push(
      key === 'stocks' ? '#0000ff' :
      key === 'realEstate' ? '#008080' :
      key === 'business' ? '#ff0000' :
      key === 'bitcoin' ? '#ffff00' :
      '#ff00ff'
    )
  })

  return [{
    values,
    labels,
    type: 'pie',
    hole: 0.4,
    marker: {
      colors: Array(values.length).fill('rgba(0,0,0,0)'),  // Transparent fill
      line: {
        color: '#33ff33',
        width: 2
      }
    },
    textinfo: 'label+percent',
    textposition: 'outside',
    outsidetextfont: {
      family: 'JetBrains Mono, monospace',
      size: 11,
      color: '#33ff33'
    },
    hoverinfo: 'label+value+percent',
    hovertemplate: '<b>%{label}</b><br>$%{value:,.0f}K<br>%{percent}<extra></extra>',
    hoverlabel: {
      bgcolor: '#333333',
      bordercolor: '#33ff33',
      font: {
        family: 'JetBrains Mono, monospace',
        size: 12,
        color: '#33ff33'
      }
    }
  }]
}

const chartLayout = {
  paper_bgcolor: 'rgba(0,0,0,1)',
  plot_bgcolor: 'rgba(0,0,0,1)',
  margin: {
    l: 50,
    r: 50,
    t: 30,
    b: 30
  },
  font: {
    family: 'JetBrains Mono, monospace',
    size: 11,
    color: '#33ff33'
  },
  showlegend: false,
  height: 300,
  annotations: [{
    text: 'Asset<br>Allocation',
    x: 0.5,
    y: 0.5,
    font: {
      family: 'JetBrains Mono, monospace',
      size: 12,
      color: '#33ff33'
    },
    showarrow: false
  }]
}

function formatLabel(key) {
  return key.replace(/([A-Z])/g, ' $1')
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')
}

function initChart() {
  if (!chartContainer.value) return
  
  const data = calculatePieData()
  const layout = {
    ...chartLayout,
    width: chartContainer.value.offsetWidth
  }

  Plotly.newPlot(chartContainer.value, data, layout, {
    displayModeBar: false,
    responsive: true
  })
}

// Add resize handler
let resizeTimeout
function handleResize() {
  clearTimeout(resizeTimeout)
  resizeTimeout = setTimeout(() => {
    if (chartContainer.value) {
      Plotly.relayout(chartContainer.value, {
        width: chartContainer.value.offsetWidth
      })
    }
  }, 100)
}

// Lifecycle hooks
onMounted(() => {
  window.addEventListener('resize', handleResize)
  initChart()
})

onUnmounted(() => {
  window.removeEventListener('resize', handleResize)
  if (chartContainer.value) {
    Plotly.purge(chartContainer.value)
  }
})

// Watch for changes in assets
watch(() => props.assets, () => {
  initChart()
}, { deep: true })
</script>

<style scoped>
.pie-chart-container {
  width: 100%;
  height: 200px;
}
</style> 